/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { ButtonProps, Theme } from '@coursera/cds-core';
import { Grid, Typography, Typography2, breakpoints, useTheme } from '@coursera/cds-core';
import { CheckIcon, RemoveIcon } from '@coursera/cds-icons';

import type {
  EntWebsiteBlockComparePlansQuery,
  EntWebsiteComparePlansFragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { NAV_BREAKPOINT } from 'bundles/ent-website/components/EntWebsitePage';
import BlockLayout, { noImage } from 'bundles/ent-website/contentful/core/BlockLayout';
import Button from 'bundles/ent-website/contentful/core/ContentfulButton';
import { entWebsiteBlockComparePlansQuery } from 'bundles/ent-website/queries/EntWebsiteComparePlans';
import { withBlockQuery } from 'bundles/ent-website/utils/withBlockQuery';
import type { BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';

type Props = BlockModuleInnerProps<EntWebsiteComparePlansFragment>;

const styles = {
  heading: () => css`
    line-height: 1 !important;
    letter-spacing: -0.03em;
    font-size: 28px;
    margin: var(--cds-spacing-150) 0;

    ${breakpoints.down('sm')} {
      font-size: 24px;
      line-height: 36px;
      letter-spacing: -0.1px;
    }
  `,
  body: () => css`
    font-size: 24px;
    line-height: 1.5;
    letter-spacing: -0.02em;

    ${breakpoints.down('md')} {
      font-size: 18px;
      margin-bottom: 40px;
    }
  `,
  userRange: () => css`
    line-height: 1.5;

    ${breakpoints.down('sm')} {
      font-size: 12px;
      line-height: 1;
      padding: 8px 0;
    }
  `,
  productName: () => css`
    font-size: 32px;
    line-height: 1;

    ${breakpoints.down('sm')} {
      font-size: 20px;
    }
  `,
  productButton: () => css`
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;

    ${breakpoints.down('sm')} {
      margin-top: 0;
      padding: 0 12px;
      align-items: unset;
    }

    a {
      ${breakpoints.down('sm')} {
        height: 40px;
        padding: 0 24px;
        font-size: 14px;
        min-width: 135px;
      }
    }
  `,
  matrixRow: () => css`
    margin: auto;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-items: center;

    ${breakpoints.down('sm')} {
      flex-wrap: wrap;
      justify-content: space-around;
    }
  `,
  matrixRowPadTop: () => css`
    > div {
      padding-top: 24px;
    }

    ${breakpoints.down('sm')} {
      > div {
        padding-top: 0;
      }
    }
  `,

  matrixRowLHS: () => css`
    justify-self: start;
    flex-grow: 1;
    min-width: 270px;

    ${breakpoints.down('sm')} {
      width: 100%;
    }
  `,

  productFeatureCategory: () => css`
    margin-bottom: 12px;

    ${breakpoints.down('sm')} {
      padding: 0 16px;
      margin-top: 40px;
      margin-bottom: 16px;
      font-size: 16px;
    }
  `,

  productFeatureBinary: () => css`
    text-align: center;
    font-size: 16px;
    line-height: 1.5;

    ${breakpoints.down('sm')} {
      margin-bottom: 12px;
      line-height: 32px;
    }

    align-items: center;
    justify-content: center;
  `,
  productFeatureBinaryPlaceholder: css`
    margin-bottom: 52px;
  `,

  mobileHidden: () => css`
    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
  desktopHidden: () => css`
    ${breakpoints.up('md')} {
      display: none;
    }
  `,
  productGroupTopSection: () => css`
    ${breakpoints.down('sm')} {
      > * {
        text-align: center;
      }
    }
  `,
  header: () => css`
    ${breakpoints.down('sm')} {
      justify-content: center;
      padding: 0 32px;
    }
  `,
  stickyGridHeader: (_: Theme, stickyHeaderOffset: number, isPinned = false) => css`
    background-color: white;
    position: sticky;
    top: ${stickyHeaderOffset}px;
    z-index: 100;
    transition: all 0.2s ease-in-out;
    box-shadow: ${isPinned ? '0px 4px 4px 0px rgba(167, 167, 167, 0.25)' : 'none'};
  `,
  stickyGridHeaderContent: () => css`
    max-width: 1440px;
    padding: 24px 64px 32px;

    ${breakpoints.down('sm')} {
      padding: 24px 12px 16px;
    }
  `,
  featureGroups: () => css`
    max-width: 1440px;
    margin: auto;
    padding: 0 64px;

    ${breakpoints.down('sm')} {
      padding: 0;
    }
  `,
  featureRow: (_: Theme, idx: number) => css`
    background-color: ${idx % 2 === 0 ? 'rgba(245, 247, 248, 1)' : 'white'};
    ${breakpoints.down('sm')} {
      padding: 0 var(--cds-spacing-150);
    }
  `,
  featureRowTitleContainer: () => css`
    width: 100%;
    flex-basis: 32.2%;
    flex-shrink: 0;
    padding: 12px;
    ${breakpoints.down('sm')} {
      padding: 12px 4px 8px;
      flex-basis: 100%;
    }
  `,
  mobileFont14px: () => css`
    ${breakpoints.down('sm')} {
      font-size: 14px;
    }
  `,
  blockStyle: () => css`
    padding-top: 8px;

    ${breakpoints.down('sm')} {
      padding-top: 24px;
    }

    .BlockLayout-contentGrid {
      padding-left: 0;
      padding-right: 0;
    }
  `,
};

const calculatedStyles = {
  productGroup: (length: number) => () => {
    const calcPerc = (100 / length).toPrecision(4);

    return css`
      width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: start;
      padding-left: 24px;
      padding-right: 24px;
      margin-left: 24px;
      text-align: center;

      ${breakpoints.down('sm')} {
        width: calc(${calcPerc}% - 12px);
        min-width: 132px;
        margin-left: 0;
        padding: 0;
      }
    `;
  },
};

export const pxToNumber = (px: string) => {
  if (px === '') {
    return 0;
  }
  if (isNaN(parseFloat(px))) return 0;

  return parseFloat(px);
};

export function ComparePlansModule({ content, assembly = {} }: Props) {
  const theme = useTheme();
  const stickyElRef = useRef<HTMLDivElement | null>(null);
  const mobileHeaderTitleRef = useRef<HTMLDivElement | null>(null);
  const comparePlansRef = useRef<HTMLDivElement | null>(null);
  const [stickyHeaderOffset, setStickyHeaderOffset] = useState<number>(0);
  const [isStickyHeaderPinned, setIsStickyHeaderPinned] = useState<boolean>(false);

  useEffect(() => {
    // Sticky header should appear below nav bar. Using this to calculate offset
    const disappearingMetaNavEl = document.querySelector<HTMLElement>('.rc-EntWebsitePageHeader-SmartScrollWrapper');
    const headerEl = document.querySelector<HTMLElement>('.rc-EntWebsitePageHeader');

    // Setting the right offset by calc header height - disappering meta nav height.
    const setStickyHeaderOffsetWrapper = () => {
      if (headerEl) {
        const newOffset =
          headerEl?.clientHeight +
          // Disappearing meta nav is not shown on mobile.
          (window.innerWidth > NAV_BREAKPOINT ? pxToNumber(disappearingMetaNavEl?.style?.top ?? '0') : 0);
        if (newOffset !== stickyHeaderOffset) {
          setStickyHeaderOffset(newOffset);
        }
      }
    };

    // Observing mutations on Meta Nav style change
    const mutationObserver = new MutationObserver((mutations) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error - style exist on target
      if (mutations[0].target?.style?.top != null) {
        setStickyHeaderOffsetWrapper();
      }
    });

    // If disappearing nav exist - listening for scroll event is not enough on Safari. Mutation observer detects when top style for disappearing bar has changed.
    if (disappearingMetaNavEl) {
      mutationObserver.observe(disappearingMetaNavEl, {
        attributes: true,
        attributeFilter: ['style'],
      });
    } else {
      window.addEventListener('scroll', setStickyHeaderOffsetWrapper);
    }

    // Setting sticky header shadow
    const scrollListener = () => {
      // Sticky header parent client height - sticky header height
      const stickyContainerScrollHeight =
        (stickyElRef?.current?.parentElement?.clientHeight ?? 0) - (stickyElRef?.current?.clientHeight ?? 0);
      // once we move of scroll container this should be false
      const isWithinScrollContainer = (stickyElRef?.current?.offsetTop ?? 0) < stickyContainerScrollHeight;
      const isPinned =
        // Mobile header title is not sticky and visible on mobile only
        (stickyElRef?.current?.offsetTop ?? 0) > (mobileHeaderTitleRef?.current?.clientHeight ?? 0) &&
        isWithinScrollContainer;
      setIsStickyHeaderPinned(isPinned);
    };

    window.addEventListener('resize', setStickyHeaderOffsetWrapper);
    window.addEventListener('scroll', scrollListener);

    return () => {
      window.removeEventListener('resize', setStickyHeaderOffsetWrapper);
      window.removeEventListener('scroll', setStickyHeaderOffsetWrapper);
      window.removeEventListener('scroll', scrollListener);
      mutationObserver.disconnect();
    };
  }, [stickyHeaderOffset]);

  const { description, subDescription, matrix, featureGroupsCollection, productsCollection } = content;

  const featureGroups = featureGroupsCollection?.items?.filter(Boolean);
  const productCollections = productsCollection?.items?.filter(Boolean);

  const header = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(() => {
    return (
      <Grid container css={[styles.matrixRowLHS, styles.header]} alignItems="center">
        <Typography css={styles.heading} variant="h1semibold">
          {description}
        </Typography>
        {subDescription && (
          <Typography2 component="p" css={styles.body}>
            {subDescription}
          </Typography2>
        )}
      </Grid>
    );
  }, [description, subDescription]);

  function contentPanel() {
    return (
      <div>
        <Grid ref={mobileHeaderTitleRef} container css={styles.desktopHidden}>
          {header()}
        </Grid>
        <div ref={stickyElRef} css={styles.stickyGridHeader(theme, stickyHeaderOffset, isStickyHeaderPinned)}>
          <div css={[styles.matrixRow, styles.stickyGridHeaderContent]}>
            <Grid
              container
              css={[
                styles.mobileHidden,
                css`
                  flex-basis: 32.2%;
                  flex-shrink: 0;
                  ${breakpoints.down('sm')} {
                    flex-basis: 100%;
                  }
                `,
              ]}
            >
              {header()}
            </Grid>
            {productCollections?.map((product, pIdx) => {
              const variant = product?.ctaVariant as ButtonProps['variant'];

              return (
                <Grid
                  container
                  alignItems="center"
                  justifyContent="center"
                  key={`product-collection-${product?.slug}`}
                  css={[calculatedStyles.productGroup(productCollections.length), styles.productGroupTopSection]}
                >
                  <Typography css={styles.productName} variant="h1semibold">
                    {product?.name}
                  </Typography>
                  <Typography2 component="p" css={styles.userRange}>
                    {product?.numUsers}
                  </Typography2>
                  <div css={styles.productButton}>
                    <Button variant={variant} component="a" href={product?.ctaTarget} module={content} position={pIdx}>
                      {product?.ctaText}
                    </Button>
                  </div>
                </Grid>
              );
            })}
          </div>
        </div>
        <div css={styles.featureGroups}>
          {featureGroups?.map((featureGroup) => {
            const features = featureGroup?.featuresCollection?.items.filter(Boolean);

            return (
              <div key={featureGroup?.slug}>
                <div css={[styles.matrixRow, styles.matrixRowPadTop]}>
                  <div css={styles.matrixRowLHS}>
                    <Typography css={styles.productFeatureCategory} variant="h2semibold">
                      {featureGroup?.name}
                    </Typography>
                  </div>

                  {productCollections?.map((product) => {
                    return (
                      <div
                        key={product?.name}
                        css={[calculatedStyles.productGroup(productCollections.length), styles.mobileHidden]}
                      >
                        <div css={styles.productFeatureBinaryPlaceholder} />
                      </div>
                    );
                  })}
                </div>
                {features?.map((feature, fIdx) => {
                  return (
                    <Grid
                      key={feature?.name}
                      alignItems="center"
                      css={[styles.matrixRow, styles.featureRow(theme, fIdx)]}
                    >
                      <div css={(styles.matrixRowLHS, styles.featureRowTitleContainer)}>
                        <Typography2 component="p" variant="bodyPrimary" css={styles.mobileFont14px}>
                          {feature?.name}
                        </Typography2>
                      </div>
                      {productCollections?.map((product) => {
                        const isChecked = matrix?.[product?.slug ?? '']?.[feature?.slug ?? ''];
                        return (
                          <Grid
                            container
                            alignItems="center"
                            justifyContent="center"
                            css={[
                              calculatedStyles.productGroup(productCollections.length),
                              styles.productFeatureBinary,
                            ]}
                          >
                            {isChecked ? (
                              <CheckIcon size="large" color="interactive" />
                            ) : (
                              <RemoveIcon size="large" color="interactive" />
                            )}
                          </Grid>
                        );
                      })}
                    </Grid>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div ref={comparePlansRef}>
      <BlockLayout fullWidth blockStyle={styles.blockStyle} assembly={noImage(assembly)} renderContent={contentPanel} />
    </div>
  );
}

// @ts-expect-error TSMIGRATION-5.0 TS2344
export const BlockComparePlansModule = withBlockQuery<EntWebsiteComparePlansFragment, EntWebsiteBlockComparePlansQuery>(
  entWebsiteBlockComparePlansQuery
)(ComparePlansModule);
